import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Guide, Loader, Pill } from "@/components/Bits";

export default function Stuck() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/stuck");
    setData(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/stuck/${id}/resolve`);
      toast.success("Marked as understood");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <Shell><Loader label="Loading your stuck list…" /></Shell>;

  return (
    <Shell>
      <div className="max-w-4xl reveal" data-testid="stuck-root">
        <p className="rule-label">What hasn't landed yet</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Things I'm stuck on</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          When the teacher tries three different explanations and an idea still doesn't land, it comes here instead of
          being quietly forgotten. Nothing here is a failure — it's a list of what to tackle next.
        </p>

        <div className="mt-6">
          <Guide>
            {data.open.length
              ? "These are the hard ones. Take them one at a time — they get smaller with a second pass."
              : "Nothing is stuck right now. Whatever the teacher can't land will show up here."}
          </Guide>
        </div>

        {!data.has_guardian && data.open.length > 0 && (
          <p className="mt-4 text-sm text-slate-500" data-testid="stuck-no-guardian">
            No guardian is linked to your account, so this list is yours alone. You can add one any time from your
            profile if you'd like another pair of eyes.
          </p>
        )}

        <div className="mt-8 space-y-4">
          {data.open.map((item) => (
            <div key={item.escalation_id} className="card-flat p-6" data-testid={`stuck-item-${item.escalation_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-display text-lg font-semibold">{item.card_title || item.topic_title}</span>
                <Pill tone="amber">{item.attempts || 3} explanations tried</Pill>
              </div>
              <p className="mt-2 text-sm text-slate-500">{item.topic_title}</p>
              {item.question && <p className="mt-3 text-sm text-slate-600">You asked: “{item.question}”</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                {item.topic_id && (
                  <Link
                    to={`/topic/${item.topic_id}`}
                    data-testid={`stuck-open-${item.escalation_id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Try it again
                  </Link>
                )}
                <button
                  data-testid={`stuck-resolve-${item.escalation_id}`}
                  onClick={() => resolve(item.escalation_id)}
                  disabled={busy === item.escalation_id}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 text-xs font-semibold hover:border-slate-500 disabled:opacity-50 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> I get it now
                </button>
              </div>
            </div>
          ))}

          {!data.open.length && <Empty title="Nothing stuck" note="Whatever the teacher couldn't land will appear here, with a way back into it." />}
        </div>

        {data.resolved.length > 0 && (
          <div className="mt-10" data-testid="stuck-resolved">
            <p className="rule-label">Cleared</p>
            <div className="mt-3 space-y-2">
              {data.resolved.map((item) => (
                <div key={item.escalation_id} className="flex items-center gap-3 px-4 py-3 card-flat">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-slate-400">{item.card_title || item.topic_title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
